import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { CalendarDays, ChevronLeft, ChevronRight, X } from 'lucide-react'
import Button from './Button'
import usePopover, { panelStyle } from './usePopover'
import { CalendarMonth } from './Calendar'
import {
  addDays,
  addMonths,
  iso,
  monthLabel,
  moveDay,
  parseIso,
  sameDay,
  startOfMonth,
  today as todayDate,
} from './calendarDates'
import { kit } from './strings'
import { displayDate } from '../lib/dates'
import './DatePicker.css'
import './DateRangeField.css'

/** The ranges people actually ask for, newest-first — each ends today or with its period. */
const PRESETS = [
  { id: 'today', range: (now) => [now, now] },
  { id: 'last7', range: (now) => [addDays(now, -6), now] },
  { id: 'last30', range: (now) => [addDays(now, -29), now] },
  { id: 'thisMonth', range: (now) => [startOfMonth(now), now] },
  {
    id: 'lastMonth',
    range: (now) => [addMonths(startOfMonth(now), -1), addDays(startOfMonth(now), -1)],
  },
  { id: 'thisYear', range: (now) => [new Date(now.getFullYear(), 0, 1), now] },
]

const order = (a, b) => (a && b && b < a ? [b, a] : [a, b])
const dayCount = (a, b) => Math.round((b - a) / 86400000) + 1

/**
 * From/to as one control, and one calendar for both ends.
 *
 * It used to be two date pickers in one shell, each with its own one-month calendar:
 * picking a range meant opening a calendar, closing it, opening the second one and
 * finding the first date again in your head. Now the shell opens ONE panel — the
 * common ranges down the side, two months across, the range washed in as you hover
 * for its end — and nothing is applied until Apply, so a list filter does not fetch
 * twice on the way to a range. A preset is a whole answer and applies on the spot.
 *
 * @param {{from: string, to: string}} value   ISO `YYYY-MM-DD`, empty for unset
 * @param {(value: {from: string, to: string}) => void} onChange
 * @param {{from?: string, to?: string}} [labels]  what each end is called while empty
 */
export default function DateRangeField({ value, onChange, labels = {}, id }) {
  const { open, setOpen, close, pos, rootRef, panelRef, id: panelId } = usePopover({ align: 'start' })

  const from = useMemo(() => parseIso(value.from), [value.from])
  const to = useMemo(() => parseIso(value.to), [value.to])
  const filled = Boolean(value.from || value.to)

  const [start, setStart] = useState(null)
  const [end, setEnd] = useState(null)
  const [hover, setHover] = useState(null)
  const [view, setView] = useState(() => startOfMonth(from ?? todayDate()))
  const [focusDay, setFocusDay] = useState(null)
  const [inGrid, setInGrid] = useState(false)

  // Every opening starts from the applied range, the earlier month on the left.
  useEffect(() => {
    if (!open) return
    setStart(from)
    setEnd(to)
    setHover(null)
    const anchor = to ?? from ?? todayDate()
    setView(addMonths(startOfMonth(anchor), from && to && from.getMonth() !== to.getMonth() ? -1 : 0))
    setFocusDay(from ?? todayDate())
  }, [open, from, to])

  const leave = () => {
    close()
    setInGrid(false)
    rootRef.current?.querySelector('.date-range__trigger')?.focus()
  }

  const apply = (a, b) => {
    onChange({ from: a ? iso(a) : '', to: b ? iso(b) : '' })
    leave()
  }

  /* The first click starts a range, the second ends it — in either order. A click
     after a whole range starts the next one rather than stretching the old. */
  const pick = (day) => {
    setFocusDay(day)
    if (!start || end) {
      setStart(day)
      setEnd(null)
      return
    }
    const [a, b] = order(start, day)
    setStart(a)
    setEnd(b)
    setHover(null)
  }

  /* While the end is still being chosen, the hovered (or keyboard-focused) day shows
     where the range would land. */
  const [lo, hi] = order(start, end ?? (start ? hover : null))
  const dayState = (day) => ({
    start: sameDay(day, lo),
    end: sameDay(day, hi),
    inRange: Boolean(lo && hi && day > lo && day < hi),
  })

  const onGridKey = (event) => {
    const next = moveDay(focusDay ?? start ?? todayDate(), event)
    if (!next) return
    event.preventDefault()
    setInGrid(true)
    setFocusDay(next)
    if (start && !end) setHover(next)
    // Two months on screen: scroll only when the day leaves both.
    if (next < view) setView(startOfMonth(next))
    else if (next >= addMonths(view, 2)) setView(addMonths(startOfMonth(next), -1))
  }

  const onPanelKey = (event) => {
    if (event.key === 'Escape') {
      event.stopPropagation()
      leave()
    }
  }

  const now = todayDate()
  const activePreset = PRESETS.find(({ range }) => {
    const [a, b] = range(now)
    return sameDay(a, start) && sameDay(b, end)
  })?.id

  const summary = lo && hi ? (
    <>
      <bdi dir="ltr">{displayDate(iso(lo))}</bdi>
      <span className="date-range__summary-dash" aria-hidden="true" />
      <bdi dir="ltr">{displayDate(iso(hi))}</bdi>
      <span className="date-range__summary-count">{kit.rangeDays(dayCount(lo, hi))}</span>
    </>
  ) : (
    <span className="date-range__summary-hint">{start ? kit.rangePickEnd : kit.rangePickStart}</span>
  )

  const slot = (text, label) => (
    <span className={`date-range__value${text ? '' : ' is-empty'}`} dir={text ? 'ltr' : undefined}>
      {text ? displayDate(text) : label}
    </span>
  )

  return (
    <div className={`date-range${filled ? ' is-filled' : ''}${open ? ' is-open' : ''}`} ref={rootRef}>
      <button
        id={id}
        type="button"
        className="date-range__trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => {
          if (open) return close()
          setInGrid(false)
          setOpen(true)
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault()
            setInGrid(true)
            setOpen(true)
          }
        }}
      >
        <CalendarDays className="date-range__icon" size={16} strokeWidth={1.75} aria-hidden="true" />
        {slot(value.from, labels.from)}
        <span className="date-range__dash" aria-hidden="true" />
        {slot(value.to, labels.to)}
      </button>

      {filled && (
        <button
          type="button"
          className="date-range__clear"
          onClick={() => onChange({ from: '', to: '' })}
          aria-label={kit.clearDate}
        >
          <X size={14} strokeWidth={2} />
        </button>
      )}

      {open &&
        createPortal(
          <div
            ref={panelRef}
            id={panelId}
            role="dialog"
            aria-label={kit.calendar}
            className="date-range__panel"
            style={panelStyle(pos)}
            onKeyDown={onPanelKey}
          >
            <div className="date-range__presets" role="group" aria-label={kit.rangePresets}>
              {PRESETS.map(({ id: presetId, range }) => (
                <button
                  key={presetId}
                  type="button"
                  className={`date-range__preset${activePreset === presetId ? ' is-active' : ''}`}
                  aria-pressed={activePreset === presetId}
                  onClick={() => apply(...range(now))}
                >
                  {kit.rangePreset[presetId]}
                </button>
              ))}
            </div>

            <div className="date-range__main">
              <div className="date-range__months">
                {[0, 1].map((offset) => {
                  const month = addMonths(view, offset)
                  return (
                    <div key={offset} className={`date-range__month date-range__month--${offset ? 'next' : 'first'}`}>
                      <div className="date-range__head">
                        {/* Previous on the first month's outer edge, next on the second's;
                            on a phone, where only one month shows, both sit on the one. */}
                        <button
                          type="button"
                          className="datepicker__nav date-range__prev"
                          tabIndex={-1}
                          onClick={() => setView((current) => addMonths(current, -1))}
                          aria-label={monthLabel.format(addMonths(view, -1))}
                        >
                          <ChevronRight className="ltr-flip" size={16} strokeWidth={2} />
                        </button>
                        <span className="date-range__title">{monthLabel.format(month)}</span>
                        <button
                          type="button"
                          className="datepicker__nav date-range__next"
                          tabIndex={-1}
                          onClick={() => setView((current) => addMonths(current, 1))}
                          aria-label={monthLabel.format(addMonths(view, 2))}
                        >
                          <ChevronLeft className="ltr-flip" size={16} strokeWidth={2} />
                        </button>
                      </div>
                      <CalendarMonth
                        view={month}
                        focusDay={focusDay}
                        autoFocus={inGrid && Boolean(pos)}
                        hideOutside
                        dayState={dayState}
                        onPick={pick}
                        onHover={(day) => start && !end && setHover(day)}
                        onKeyDown={onGridKey}
                      />
                    </div>
                  )
                })}
              </div>

              <div className="date-range__foot">
                <p className="date-range__summary" aria-live="polite">
                  {summary}
                </p>
                <div className="date-range__actions">
                  <Button size="sm" variant="ghost" onClick={() => apply(null, null)}>
                    {kit.clearField}
                  </Button>
                  <Button size="sm" disabled={!(start && end)} onClick={() => apply(start, end)}>
                    {kit.apply}
                  </Button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  )
}
